const REGIME_TO_BIAS = {
  BULL: "BULLISH",
  BEAR: "BEARISH",
  NEUTRAL: "NEUTRAL",
};

const DECISION_TO_BIAS = {
  BUY: "LONG",
  LONG: "LONG",
  SELL: "SHORT",
  SHORT: "SHORT",
  HOLD: "WATCH",
  WATCH: "WATCH",
};

const EMPTY_DISTRIBUTION = {
  bars: [],
  winRate: 0,
  avgReturn: 0,
  rr: 0,
  totalTrades: 0,
};

const EMPTY_PNL = {
  series: [],
  peak: 0,
  current: 0,
  drawdown: 0,
  trough: 0,
};

const EMPTY_REPORT = {
  dominant: "No scheduled analysis in database.",
  risk: "—",
  disagreement: "—",
  action: "—",
};

function formatTime(timestamp) {
  if (!timestamp) return "--:--";
  const d = new Date(timestamp);
  if (Number.isNaN(d.getTime())) return "--:--";
  return d.toISOString().slice(11, 16);
}

function formatOutcome(pnl) {
  if (pnl == null || pnl === "") return "OPEN";
  const n = Number(pnl);
  if (Number.isNaN(n)) return String(pnl);
  if (n > 0) return `+${n.toFixed(1)}%`;
  if (n < 0) return `${n.toFixed(1)}%`;
  return "BREAKEVEN";
}

function parseMetadata(metadata) {
  if (!metadata) return {};
  if (typeof metadata === "object") return metadata;
  try {
    return JSON.parse(metadata);
  } catch {
    return {};
  }
}

export function mapDailyStateToStats(dailyState, tradingConfig) {
  const capital = Number(dailyState?.available_capital_idr) || 0;
  const profitPct = Number(dailyState?.current_daily_profit_pct) || 0;
  const deployedPct = Number(dailyState?.total_deployed_pct) || 0;
  const regime = tradingConfig?.market_regime || "NEUTRAL";
  const minScore = Number(tradingConfig?.min_composite_score_to_enter) || 60;
  const maxLoss = Number(tradingConfig?.max_daily_loss_pct) || 2.5;
  const targetPct = Number(tradingConfig?.daily_profit_target_pct) || 1.75;
  const pnlIdr = capital > 0 ? (capital * profitPct) / 100 : 0;

  return {
    pnl: +pnlIdr.toFixed(0),
    gain: +profitPct.toFixed(2),
    confidence: +Math.min(99, Math.max(minScore, minScore + profitPct * 4)).toFixed(1),
    marketBias: REGIME_TO_BIAS[regime] || "NEUTRAL",
    newsPressure: +Math.min(95, deployedPct + 20).toFixed(1),
    crowdSentiment: +Math.min(92, 50 + profitPct * 8).toFixed(1),
    whaleSignal: +Math.min(98, minScore + deployedPct * 0.4).toFixed(1),
    riskScore: +Math.min(90, maxLoss * 10 + deployedPct * 0.3).toFixed(1),
    agentDisagreement: +Math.min(45, Math.abs(profitPct - targetPct) * 6 + 8).toFixed(1),
    capitalIdr: capital,
    deployedPct,
    status: dailyState?.status || "—",
    tradeDate: dailyState?.trade_date || "—",
    regime,
    dailyTargetPct: targetPct,
    maxDailyLossPct: maxLoss,
  };
}

export function mapDecisionLogRow(row, index) {
  const meta = parseMetadata(row.metadata);
  const decision = (row.decision || "").toUpperCase();
  const bias = DECISION_TO_BIAS[decision] || "WATCH";
  const entry = meta.entry ?? meta.entry_zone ?? row.size_idr;
  const stop = meta.stop ?? meta.stop_loss ?? meta.stop_loss_level;

  return {
    id: row.id ?? index,
    time: formatTime(row.timestamp),
    pair: row.emiten || "—",
    bias,
    decision,
    agent: row.agent || "—",
    reason: row.reason || "—",
    confidence: (meta.confidence || "MEDIUM").toUpperCase(),
    entry: entry != null ? +Number(entry).toFixed(0) : 0,
    stop: stop != null ? +Number(stop).toFixed(0) : 0,
    outcome: meta.outcome || formatOutcome(row.pnl_at_decision),
  };
}

export function mapDecisionLogRows(rows) {
  return (rows || []).map((row, i) => mapDecisionLogRow(row, i));
}

export function mapActivePositionRow(row, index) {
  return {
    id: `pos-${row.ticker}-${index}`,
    time: formatTime(row.updated_at),
    pair: row.ticker,
    bias: Number(row.unrealized_pnl_pct) >= 0 ? "LONG" : "SHORT",
    decision: "POSITION",
    agent: "portfolio",
    reason: `Allocated ${row.allocated_pct}%`,
    confidence: "HIGH",
    entry: +Number(row.avg_buy_price || 0).toFixed(0),
    stop: row.stop_loss_level != null ? +Number(row.stop_loss_level).toFixed(0) : 0,
    outcome: formatOutcome(row.unrealized_pnl_pct),
    lotHeld: row.lot_held,
    currentPrice: row.current_price,
    takeProfit: row.take_profit_level,
    allocatedPct: row.allocated_pct,
    unrealizedPnlPct: row.unrealized_pnl_pct,
  };
}

export function mapActivePositionRows(rows) {
  return (rows || []).map((row, i) => mapActivePositionRow(row, i));
}

export function mapScheduledAnalysisToReport(rows) {
  if (!rows?.length) return { ...EMPTY_REPORT };

  const sorted = [...rows].sort((a, b) => {
    const ra = a.priority_rank ?? 9999;
    const rb = b.priority_rank ?? 9999;
    return ra - rb;
  });

  const top = sorted[0];
  const scores = sorted
    .map((r) => Number(r.ai_consensus_score))
    .filter((n) => !Number.isNaN(n));

  const minScore = scores.length ? Math.min(...scores) : 0;
  const maxScore = scores.length ? Math.max(...scores) : 0;
  const spread = +(maxScore - minScore).toFixed(1);

  const tech =
    top.technical_structure && typeof top.technical_structure === "object"
      ? top.technical_structure.summary || top.technical_structure.trend
      : null;

  return {
    dominant: `${top.symbol || top.name || top.stock_id}: composite ${Number(top.composite_score || 0).toFixed(1)} · priority #${top.priority_rank ?? "—"}.`,
    risk:
      top.bandamon_flow ||
      tech ||
      `Monitor ${top.symbol || "watchlist"} flow before increasing size.`,
    disagreement: `${spread}% spread in AI consensus across ${sorted.length} scheduled names.`,
    action:
      top.target_size_idr != null
        ? `Target allocation IDR ${Number(top.target_size_idr).toLocaleString("en-US", { maximumFractionDigits: 0 })} on ${top.symbol || top.stock_id}.`
        : `Review ${top.symbol || top.stock_id} at last price ${top.last_price ?? "—"}.`,
  };
}

const GRAPH_CENTER_X = 300;
const GRAPH_CENTER_Y = 210;

function pickExecution(meta) {
  if (!meta || typeof meta !== "object") return {};
  const exec = meta.execution && typeof meta.execution === "object" ? meta.execution : {};
  return exec;
}

export function mapScheduledAnalysisToGraph(rows) {
  const list = (rows || []).filter((r) => r && r.symbol);
  if (!list.length) return { nodes: [], links: [], bias: "NEUTRAL", topPick: null };

  const scoreOf = (r) =>
    Number(r.composite_priority_score ?? r.composite_score ?? 0) || 0;

  const scores = list.map(scoreOf);
  const maxScore = Math.max(...scores, 1);
  const minScore = Math.min(...scores, 0);
  const spread = +(maxScore - minScore).toFixed(1);

  const sorted = [...list].sort((a, b) => scoreOf(b) - scoreOf(a));
  const hub = sorted[0];

  const nodes = list.map((row, i) => {
    const score = scoreOf(row);
    const norm = maxScore > 0 ? score / maxScore : 0;
    const rank = row.priority_rank != null ? Number(row.priority_rank) : null;
    const hasTarget = rank != null && row.target_size_idr != null;
    const group = hasTarget
      ? "priority"
      : Number(row.composite_score) >= 50
        ? "watch"
        : "neutral";

    const angle = (i / list.length) * Math.PI * 2;
    const radius = 70 + (1 - norm) * 110;
    const size = 14 + norm * 16;

    return {
      id: String(row.stock_id ?? row.symbol ?? i),
      symbol: row.symbol,
      name: row.name || row.symbol,
      label: row.symbol,
      group,
      score: +score.toFixed(1),
      rank,
      size,
      consensus: row.ai_consensus_score != null ? Number(row.ai_consensus_score) : null,
      composite: row.composite_score != null ? Number(row.composite_score) : null,
      x: GRAPH_CENTER_X + Math.cos(angle) * radius,
      y: GRAPH_CENTER_Y + Math.sin(angle) * radius,
      offset: angle,
      speed: 0.3 + (i % 5) * 0.12,
      ampX: 8 + (i % 4) * 4,
      ampY: 6 + (i % 3) * 4,
    };
  });

  const hubId = String(hub.stock_id ?? hub.symbol);
  const links = nodes
    .filter((n) => n.id !== hubId)
    .map((n) => [hubId, n.id]);

  const flow = (hub.bandamon_flow || "").toUpperCase();
  const bias = flow.includes("OUT") || flow === "BEARISH" ? "BEARISH" : flow.includes("IN") || flow === "BULLISH" ? "BULLISH" : "NEUTRAL";

  return {
    nodes,
    links,
    bias,
    spread,
    priorityCount: nodes.filter((n) => n.group === "priority").length,
    topPick: {
      symbol: hub.symbol,
      score: +scoreOf(hub).toFixed(1),
      rank: hub.priority_rank ?? null,
    },
  };
}

function toNumOrNull(value) {
  if (value == null || value === "") return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
}

export function getAnalysisLevels(rows, positions, symbol) {
  if (!symbol) return null;

  const row = (rows || []).find((r) => r.symbol === symbol);
  const position = (positions || []).find((p) => p.ticker === symbol);
  if (!row && !position) return null;

  const meta = parseMetadata(row?.technical_structure);
  const exec = pickExecution(meta);

  const lastPrice =
    toNumOrNull(row?.last_price) ??
    toNumOrNull(exec.current_price) ??
    toNumOrNull(position?.current_price) ??
    0;

  return {
    symbol,
    name: row?.name || symbol,
    lastPrice,
    current:
      toNumOrNull(position?.current_price) ??
      toNumOrNull(exec.current_price) ??
      lastPrice,
    entry:
      toNumOrNull(position?.avg_buy_price) ?? toNumOrNull(exec.entry_price),
    stop:
      toNumOrNull(position?.stop_loss_level) ?? toNumOrNull(exec.stop_loss),
    takeProfit:
      toNumOrNull(position?.take_profit_level) ?? toNumOrNull(exec.take_profit),
    vmap: toNumOrNull(row?.vmap),
    compositeScore: toNumOrNull(row?.composite_score),
    consensus: toNumOrNull(row?.ai_consensus_score),
    priorityRank: toNumOrNull(row?.priority_rank),
    targetSizeIdr: toNumOrNull(row?.target_size_idr),
    bandamonFlow: row?.bandamon_flow || null,
    isHeld: Boolean(position),
  };
}

export function mapDecisionLogToPnlSeries(decisions) {
  const sorted = [...(decisions || [])].sort(
    (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
  );
  if (!sorted.length) return { ...EMPTY_PNL };

  let cumulative = 0;
  const series = sorted.map((row, i) => {
    const delta = Number(row.pnl_at_decision) || 0;
    cumulative += delta;
    return { round: i + 1, pnl: +cumulative.toFixed(2) };
  });

  const peak = Math.max(...series.map((s) => s.pnl));
  const current = series[series.length - 1].pnl;
  const trough = Math.min(...series.map((s) => s.pnl));
  const drawdown = +(peak - current).toFixed(2);

  return { series, peak, current, drawdown, trough };
}

export function mapDecisionLogToDistribution(decisions) {
  const rows = decisions || [];
  if (!rows.length) return { ...EMPTY_DISTRIBUTION };

  const buckets = {
    BUY: 0,
    SELL: 0,
    HOLD: 0,
    WIN: 0,
    LOSS: 0,
  };

  rows.forEach((row) => {
    const d = (row.decision || "").toUpperCase();
    if (d === "BUY" || d === "LONG") buckets.BUY += 1;
    else if (d === "SELL" || d === "SHORT") buckets.SELL += 1;
    else buckets.HOLD += 1;

    const pnl = Number(row.pnl_at_decision);
    if (!Number.isNaN(pnl)) {
      if (pnl > 0) buckets.WIN += 1;
      else if (pnl < 0) buckets.LOSS += 1;
    }
  });

  const total = rows.length;
  const winRate = buckets.WIN + buckets.LOSS > 0
    ? +((buckets.WIN / (buckets.WIN + buckets.LOSS)) * 100).toFixed(1)
    : 0;

  const bars = [
    { label: "BUY", value: buckets.BUY, pct: +((buckets.BUY / total) * 100).toFixed(1) },
    { label: "SELL", value: buckets.SELL, pct: +((buckets.SELL / total) * 100).toFixed(1) },
    { label: "HOLD", value: buckets.HOLD, pct: +((buckets.HOLD / total) * 100).toFixed(1) },
    { label: "WIN", value: buckets.WIN, pct: +((buckets.WIN / total) * 100).toFixed(1) },
    { label: "LOSS", value: buckets.LOSS, pct: +((buckets.LOSS / total) * 100).toFixed(1) },
  ].filter((b) => b.value > 0);

  const avgPnl =
    rows.reduce((s, r) => s + (Number(r.pnl_at_decision) || 0), 0) / total;

  return {
    bars: bars.length ? bars : [{ label: "DECISIONS", value: total, pct: 100 }],
    winRate,
    avgReturn: +avgPnl.toFixed(2),
    rr: buckets.LOSS > 0 ? +(buckets.WIN / buckets.LOSS).toFixed(2) : buckets.WIN,
    totalTrades: total,
  };
}

export function mapPositionsToPriceChart(positions, selectedTicker = null) {
  const list = (positions || []).filter(
    (p) => !selectedTicker || p.ticker === selectedTicker,
  );
  if (!list.length) return { bars: [], lastPrice: 0, ticker: selectedTicker };

  const bars = list.map((p) => ({
    ticker: p.ticker,
    avgBuy: Number(p.avg_buy_price) || 0,
    current: Number(p.current_price) || 0,
    stop: Number(p.stop_loss_level) || 0,
    takeProfit: Number(p.take_profit_level) || 0,
  }));

  const first = bars[0];
  return {
    bars,
    lastPrice: first.current,
    ticker: first.ticker,
  };
}

export function mapArticleRow(row) {
  const tags = Array.isArray(row.ticker_tags)
    ? row.ticker_tags
    : row.ticker_tags
      ? [row.ticker_tags]
      : [];

  return {
    id: row.url,
    title: row.title || "Untitled",
    source: row.source || "—",
    publishedAt: row.published_time,
    sentiment: row.ai_sentiment,
    score: row.ai_score,
    category: row.ai_category || "—",
    tickers: tags.join(", ") || "—",
    reason: row.ai_reason || "",
  };
}

export function mapArticleRows(rows) {
  return (rows || []).map(mapArticleRow);
}

export function mapXPostRow(row) {
  return {
    id: row.tweet_id,
    source: row.source,
    user: row.user_screen_name || "—",
    text: row.text || "",
    createdAt: row.item_created_at || row.scraped_at,
    retweets: row.retweet_count ?? 0,
    likes: row.favorite_count ?? 0,
  };
}

export function mapXPostRows(rows) {
  return (rows || []).map(mapXPostRow);
}

export function buildDashboardView(raw) {
  const dailyState = raw.dailyState || {};
  const tradingConfig = raw.tradingConfig || {};
  const decisions = raw.decisions || [];
  const positions = raw.positions || [];
  const analysis = raw.analysis || [];

  const stats =
    raw.dailyState || raw.tradingConfig
      ? mapDailyStateToStats(dailyState, tradingConfig)
      : null;

  let log = mapDecisionLogRows(decisions);
  if (!log.length && positions.length) {
    log = mapActivePositionRows(positions);
  }

  const report = mapScheduledAnalysisToReport(analysis);
  const analysisGraph = mapScheduledAnalysisToGraph(analysis);
  const pnl = mapDecisionLogToPnlSeries(decisions);
  const distribution = mapDecisionLogToDistribution(decisions);
  const articles = mapArticleRows(raw.articles);
  const xPosts = mapXPostRows(raw.xPosts);

  const tickers = [
    ...new Set([
      ...positions.map((p) => p.ticker),
      ...analysis.map((a) => a.symbol).filter(Boolean),
    ]),
  ];

  return {
    stats,
    log,
    report,
    analysisGraph,
    pnl,
    distribution,
    articles,
    xPosts,
    positions,
    analysis,
    tickers,
    dailyState,
    tradingConfig,
  };
}

export const emptyDashboardView = () =>
  buildDashboardView({
    dailyState: null,
    tradingConfig: null,
    positions: [],
    decisions: [],
    analysis: [],
    articles: [],
    xPosts: [],
  });
